import { computed, inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { GetPayables, PaymentsState } from '@aiwyn/common/payments';
import { CommonSettingsState } from '@aiwyn/common/settings';
import { ConfigService } from '@aiwyn/config';
import { LoadableHelper } from '@aiwyn/shared';

@Injectable({
  providedIn: 'root'
})
export class AutopayEligibilityService {

  private store = inject(Store);
  private configService = inject(ConfigService);

  private payables = this.store.selectSignal(PaymentsState.getPayablesLoadable);
  private autopayClients = this.store.selectSignal(CommonSettingsState.getAutoPayClientsLoadable);

  /**
   * Determines whether the user has invoices linked to business entities that do not allow for autopay and that those ineligible invoices
   * are also linked to a client that is set up to autopay invoices.
   */
  autopayClientIdsWithIneligibleBusinessEntities = computed(() => {
    // Validate autopay is even enabled
    if (!this.configService.autoPayEnabled || !this.configService.paymentsV4Enabled) {
      return [];
    }

    // Make sure everything is loaded
    if (!LoadableHelper.isLoaded(this.payables().status) || !LoadableHelper.isLoaded(this.autopayClients().status)) {
      return [];
    }

    // If there are no active autopay clients, no need for a warning
    const activeAutopays = (this.autopayClients()?.data || []).filter(({ active }) => active);
    if (activeAutopays.length === 0) {
      return [];
    }

    const activeAutopayClientIds = activeAutopays.map(({ clientId }) => clientId);

    return (this.payables()?.data || [])
      .filter(payable => payable.entityAutopayEnabled === false && activeAutopayClientIds.includes(payable.clientId))
      .map(payable => payable.clientId)
      .reduce((uniqueIds, id) => uniqueIds.includes(id) ? uniqueIds : [...uniqueIds, id], []);
  });

  hasIneligibleAutopayPayables = computed(() => this.autopayClientIdsWithIneligibleBusinessEntities().length > 0);

  constructor() {
    // No forceRefresh: eligibility only needs payables to be AVAILABLE. A forced refetch from this
    // root-service constructor rebuilds the unpaid list mid-interaction, which discards the payer's
    // invoice selection (resetting to the default all-selected basket) and the computed cross-entity lock.
    this.store.dispatch(new GetPayables());
  }
}
